import { format } from 'date-fns'
import { without } from 'lodash'
import React, { Fragment, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Popover } from 'react-tiny-popover'

import { DotSpan } from '@src/components/DotSpan/DotSpan'
import { FulfilmentOverlay } from '@src/components/FulfilmentOverlay/FulfilmentOverlay'
import { DeliveryLocationSection } from '@src/components/ServicePopover/DeliveryLocationSection'
import { LocationLabel } from '@src/components/ServicePopover/LocationLabel'
import { SectionHeader } from '@src/components/ServicePopover/SectionHeader'
import { SectionContentContainer } from '@src/components/BottomSheet/BottomSheet.styles'
import { WhenPopover } from '@src/components/ServicePopover/WhenPopover'
import {
  LocationType,
  MerchantType,
  NarrowFulfilmentMethodInputType,
} from '@src/graphql-types'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { FulfilmentFilterWhenType } from '@src/hooks/useFulfilmentFilter/validation'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { BagDeliverySVG } from '@src/images/BagDelivery'
import { jwtVar } from '@src/models/customer/jwt'
import { ALL_FULFILMENT_FILTER_WHEN_TYPES } from '@src/utils/fulfilment'

import {
  Container,
  DesktopDropdownContent,
  DropdownInnerContainer,
  InnerContainer,
  LabelContainer,
  LargeLabel,
  StyledClockSVG,
  StyledDoubleArrowSVG,
  StyledLocationSVG,
} from './DesktopNavigation.styles'

import { Search } from '../SearchBar/Search'
import { useReactiveVar } from '@apollo/client/react/hooks/useReactiveVar'
import { useBottomSheetStack } from '@src/hooks/useBottomSheetStack/useBottomSheetStack'

enum ActiveMenu {
  SERVICE = 'SERVICE',
  TIMESLOT = 'TIMESLOT',
  LOCATION = 'LOCATION',
}

export const DesktopNavigation: React.FC = () => {
  // Set active menu: active menu state styling should persist after menu is closed
  const jwt = useReactiveVar(jwtVar)
  const [activeMenu, setActiveMenu] = useState<ActiveMenu | null>(null)
  const fulfilmentFilter = useFulfilmentFilter()
  const marketplace = useMarketplace()
  const { t } = useTranslation('serviceNavigation')
  const { postcodeBottomSheetController } = useBottomSheetStack()
  const location = useLocation()
  const hasCategoriesParam = new URLSearchParams(location.search).has(
    'categories'
  )

  const merchantTypeMap = {
    [MerchantType.RESTAURANT]: t('all_restaurants'),
    [MerchantType.RETAIL]: t('all_outlets'),
  }

  const allFulfilmentMethods = fulfilmentFilter.availableFulfilmentMethods
  const selectedFulfilmentMethods: NarrowFulfilmentMethodInputType[] =
    fulfilmentFilter.savedData.sortedFulfilmentMethods.filter(method =>
      allFulfilmentMethods.includes(method)
    )
  const isAllOrNoneSelected =
    fulfilmentFilter.data.nonEmptyFulfilmentMethods.length >=
      allFulfilmentMethods.length || selectedFulfilmentMethods.length === 0
  const merchantType = marketplace.merchantType
  const allOutletsText =
    marketplace.allOutletsText || merchantTypeMap[merchantType] || t('all')

  // categories page does not allow service selection
  // so display All, Anytime, and Everywhere
  // and disable the buttons
  if (hasCategoriesParam) {
    return (
      <Container>
        <InnerContainer isActive={false} disabled>
          <LabelContainer>
            <BagDeliverySVG id="service" />
            <LargeLabel>{allOutletsText}</LargeLabel>
          </LabelContainer>
        </InnerContainer>
        <InnerContainer isActive={false} disabled>
          <LabelContainer>
            <StyledClockSVG id="time" />
            <LargeLabel>{t('anytime')}</LargeLabel>
          </LabelContainer>
        </InnerContainer>
        <InnerContainer isActive={activeMenu === ActiveMenu.LOCATION} disabled>
          <LabelContainer>
            <StyledLocationSVG id="location" />
            <LargeLabel>{t('everywhere')}</LargeLabel>
          </LabelContainer>
        </InnerContainer>
        <Search />
      </Container>
    )
  }

  const whenMenuLabel = () => {
    switch (fulfilmentFilter.data.when.type) {
      case FulfilmentFilterWhenType.ANYTIME:
        return t('anytime')
      case FulfilmentFilterWhenType.ASAP:
        return t('now')
      case FulfilmentFilterWhenType.PREORDER:
        return format(fulfilmentFilter.data.when.preorderDate, 'EEE dd MMM')
    }
  }

  // normal outlet list page, so display interactive buttons
  return (
    <Container>
      {/* Service Popover */}
      <Popover
        isOpen={activeMenu === ActiveMenu.SERVICE}
        positions={['bottom', 'right', 'left', 'top']} // preferred positions by priority
        align={'center'}
        onClickOutside={() => {
          setActiveMenu(prevMenu => (prevMenu !== activeMenu ? prevMenu : null))
        }}
        content={({ childRect }) => (
          <DropdownInnerContainer width={`${childRect.width}px`}>
            <DesktopDropdownContent>
              <SectionHeader
                header={t('list_service_header')}
                subheader={t('list_service_subheader')}
              />
              <SectionContentContainer>
                <FulfilmentOverlay
                  availableFulfilmentMethods={allFulfilmentMethods}
                  selectedFulfilmentMethods={selectedFulfilmentMethods}
                  setSelectedFulfilmentMethods={
                    fulfilmentFilter.setActiveFulfilmentMethods
                  }
                />
              </SectionContentContainer>
            </DesktopDropdownContent>
          </DropdownInnerContainer>
        )}
      >
        <InnerContainer
          isActive={activeMenu === ActiveMenu.SERVICE}
          onClick={() => {
            setActiveMenu(ActiveMenu.SERVICE)
          }}
          data-test-id="desktop-navigation-select-service"
        >
          <LabelContainer>
            <BagDeliverySVG id="service" />
            <LargeLabel>
              {isAllOrNoneSelected
                ? allOutletsText
                : selectedFulfilmentMethods.map((fulfilment, index) => (
                    <Fragment key={fulfilment}>
                      {index > 0 && <DotSpan />}
                      {t(fulfilment.toLowerCase())}
                    </Fragment>
                  ))}
            </LargeLabel>
          </LabelContainer>
          <StyledDoubleArrowSVG id={`doubleArrow-service`} />
        </InnerContainer>
      </Popover>

      {/* WHEN Popover */}
      {
        // Display WHEN filter if DELIVERY or COLLECTION service is one of the selected fulfilment methods
        fulfilmentFilter.data.nonEmptyFulfilmentMethods.some(fulfilmentMethod =>
          [
            NarrowFulfilmentMethodInputType.DELIVERY,
            NarrowFulfilmentMethodInputType.COLLECTION,
          ].includes(fulfilmentMethod)
        ) && (
          <Popover
            isOpen={activeMenu === ActiveMenu.TIMESLOT}
            onClickOutside={() =>
              setActiveMenu(prevMenu =>
                prevMenu !== activeMenu ? prevMenu : null
              )
            }
            align={'center'}
            positions={['bottom', 'right', 'left', 'top']} // preferred positions by priority
            content={({ childRect }) => (
              <DropdownInnerContainer width={`${childRect.width}px`}>
                <DesktopDropdownContent>
                  <SectionHeader
                    header={t('list_time_header')}
                    subheader={t('list_time_subheader')}
                  />
                  <SectionContentContainer>
                    <WhenPopover
                      availableWhenTypes={ALL_FULFILMENT_FILTER_WHEN_TYPES}
                      selectedWhen={fulfilmentFilter.data.when}
                      onDone={when => {
                        fulfilmentFilter.setWhen(when)
                        setActiveMenu(null)
                      }}
                      showSelection={false}
                    />
                  </SectionContentContainer>
                </DesktopDropdownContent>
              </DropdownInnerContainer>
            )}
          >
            <InnerContainer
              isActive={activeMenu === ActiveMenu.TIMESLOT}
              onClick={() => {
                setActiveMenu(ActiveMenu.TIMESLOT)
              }}
              data-test-id="desktop-navigation-select-when"
            >
              <LabelContainer>
                <StyledClockSVG id="time" />
                <LargeLabel>{whenMenuLabel()}</LargeLabel>
              </LabelContainer>
              <StyledDoubleArrowSVG id={`doubleArrow-when`} />
            </InnerContainer>
          </Popover>
        )
      }

      {/* WHERE Popover */}
      <Popover
        isOpen={
          activeMenu === ActiveMenu.LOCATION ||
          postcodeBottomSheetController.isOpen
        }
        positions={['bottom', 'right', 'left', 'top']} // preferred positions by priority
        onClickOutside={() =>
          setActiveMenu(prevMenu => (prevMenu !== activeMenu ? prevMenu : null))
        }
        align={'center'}
        content={({ childRect }) => (
          <DropdownInnerContainer width={`${childRect.width}px`}>
            <DesktopDropdownContent>
              <SectionHeader
                header={t('list_location_header')}
                subheader={t('location_subheader')}
              />
              <SectionContentContainer>
                <DeliveryLocationSection
                  availableLocationTypes={
                    jwt
                      ? marketplace.availableLocationTypes
                      : without(
                          marketplace.availableLocationTypes,
                          LocationType.ADDRESS
                        )
                  }
                  selectedLocation={fulfilmentFilter.data.where.location}
                  setSelectedLocation={locationType => {
                    setActiveMenu(null)
                    fulfilmentFilter.setLocationType(locationType)
                  }}
                  historicalData={fulfilmentFilter.data.where.historicalData}
                  showSubtitles={false}
                  showSelection={false}
                  handleCancelWhenNavigatingAway={() => setActiveMenu(null)}
                />
              </SectionContentContainer>
            </DesktopDropdownContent>
          </DropdownInnerContainer>
        )}
      >
        <InnerContainer
          isActive={activeMenu === ActiveMenu.LOCATION}
          onClick={() => {
            setActiveMenu(ActiveMenu.LOCATION)
          }}
          data-test-id="desktop-navigation-select-where"
        >
          <LabelContainer>
            <StyledLocationSVG id="location" />
            <LargeLabel>
              <LocationLabel
                fulfilmentFilterWhere={fulfilmentFilter.data.where}
              />
            </LargeLabel>
          </LabelContainer>

          <StyledDoubleArrowSVG id={`doubleArrow-where`} />
        </InnerContainer>
      </Popover>
      <Search />
    </Container>
  )
}
